import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import SEO from './SEO';

const LOGO_URL = '/couplein_logo.png';

/**
 * Shared layout for legal pages (Privacy, Terms).
 * Renders a brand navbar, prose-styled content area, and a slim footer.
 */
import Navbar from './Navbar';

export const LegalLayout = ({ title, effectiveDate, seoTitle, seoDescription, path, children }) => {
  useEffect(() => {
    document.getElementById('root')?.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title={seoTitle || title}
        description={seoDescription}
        path={path}
      />

      {/* Unified Public Navbar */}
      <Navbar />

      {/* Content */}
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-16">
        <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">{title}</h1>
        {effectiveDate && (
          <p className="text-sm text-gray-500 mb-8">Effective {effectiveDate}</p>
        )}
        <div className="legal-prose text-gray-700 leading-relaxed space-y-6">
          {children}
        </div>

        <div className="mt-12 pt-8 border-t border-gray-100 flex flex-wrap gap-x-6 gap-y-2 text-sm">
          <Link to="/privacy" className="text-gray-600 hover:text-pink-500" data-testid="legal-link-privacy">Privacy Policy</Link>
          <Link to="/terms" className="text-gray-600 hover:text-pink-500" data-testid="legal-link-terms">Terms of Service</Link>
          <Link to="/blog" className="text-gray-600 hover:text-pink-500">Blog</Link>
          <a href="mailto:info@coupleinapp.com" className="text-gray-600 hover:text-pink-500" data-testid="legal-link-contact">Contact</a>
        </div>
      </main>

      <footer className="border-t border-gray-100 py-8 mt-8">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-xs text-gray-400">
          © {new Date().getFullYear()} Couple In LTD. All rights reserved.
        </div>
      </footer>
    </div>
  );
};

// Convenience wrappers for consistent typography inside legal pages
export const Section = ({ number, title, children }) => (
  <section className="space-y-3" data-testid={`legal-section-${number}`}>
    <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 mt-8">
      {number}. {title}
    </h2>
    <div className="space-y-3">{children}</div>
  </section>
);

export const P = ({ children }) => <p className="text-base">{children}</p>;

export const UL = ({ children }) => (
  <ul className="list-disc pl-6 space-y-1.5 text-base marker:text-pink-400">{children}</ul>
);

export const LI = ({ children }) => <li>{children}</li>;

export const B = ({ children }) => <strong className="font-semibold text-gray-900">{children}</strong>;

export default LegalLayout;
